import { memo, useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import { PlayFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/PlayFilled";
import { GalleryVideoObject } from "../../Galleries/utils/utils";
import { dispatchGalleryMonetateQEvent } from "@/components/Galleries/components/Analytics/dispatchGalleryMonetateQEvent";
import styles from "./ZoomModalVideo.module.scss";

type ZoomModalVideoProps = {
  video: GalleryVideoObject;
  index: number;
  total: number;
  scrollToIndex: (index: number) => void;
  // Ensures at most one video plays at a time across carousel slides
  isActive?: boolean;
  onPlayStateChange?: (index: number, isPlaying: boolean) => void;
};

export const ZoomModalVideo = memo(function GalleryVideo({
  video,
  index,
  total,
  scrollToIndex,
  isActive,
  onPlayStateChange,
}: ZoomModalVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    scrollToIndex(index);
    videoRef.current?.play();
    setIsPlaying(true);
    onPlayStateChange?.(index, true);
    dispatchGalleryMonetateQEvent("VideoInteraction", "videoModal", undefined);
  }, [index, onPlayStateChange, scrollToIndex]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    onPlayStateChange?.(index, false);
  }, [index, onPlayStateChange]);

  // Another slide's video became active - stop this one so only one plays at a time
  useEffect(() => {
    if (isPlaying && isActive === false) {
      videoRef.current?.pause();
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  return (
    <div className={styles.videoWrapper}>
      <video
        ref={videoRef}
        className={styles.video}
        controls={isPlaying}
        poster={video.posterUrl}
        src={video.videoUrl}
        aria-label={`Product video ${index + 1} of ${total}, ${video.title}`}
        data-testid={`gallery:zoom:modal:video-${index}`}
        onClick={() => scrollToIndex(index)}
        onPause={handlePause}
        onEnded={handlePause}
      />
      {!isPlaying && (
        <>
          <button
            aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
            data-testid={`gallery:zoom:modal:video-play-button-${index}`}
            className={styles.videoPlayButton}
            onClick={handlePlay}
          ></button>
          <IconButton
            icon={PlayFilled}
            aria-label={`Play video ${index + 1} of ${total}, ${video.title}`}
            data-testid={`gallery:zoom:modal:video-play-icon-${index}`}
            className={styles.playButton}
            onClick={handlePlay}
            background="tonal"
            variant="inverted"
          />
        </>
      )}
    </div>
  );
});
